import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Lock, User, Eye, EyeOff, HelpCircle, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isRegister, setIsRegister] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: ''
  });
  const [forgotEmail, setForgotEmail] = useState('');
  const [recoveredInfo, setRecoveredInfo] = useState(null);

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const endpoint = isRegister ? '/auth/register' : '/auth/login';
      const response = await axios.post(`${API}${endpoint}`, formData, {
        withCredentials: true
      });
      
      // Check if user must reset password on first login
      if (response.data.must_reset_password) {
        toast.info('Please set a new password for your account');
        navigate('/admin/settings', { 
          state: { 
            user: response.data, 
            forcePasswordReset: true 
          } 
        });
      } else {
        toast.success(isRegister ? 'Account created!' : 'Welcome back!');
        navigate('/admin/dashboard', { state: { user: response.data } });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/admin/auth-callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) {
      toast.error('Please enter your email');
      return;
    }
    
    setIsLoading(true);
    try {
      // First try to recover username
      const usernameRes = await axios.get(`${API}/auth/recover-username?email=${encodeURIComponent(forgotEmail)}`);
      
      // Then get password reset token
      const passwordRes = await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      
      if (usernameRes.data.success) {
        setRecoveredInfo({
          username: usernameRes.data.username,
          name: usernameRes.data.name,
          resetToken: passwordRes.data.reset_token
        });
        toast.success('Account found! See your credentials below.');
      } else {
        toast.error('No account found with this email');
      }
    } catch (error) {
      toast.error('Could not find account. Contact admin for help.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (newPassword) => {
    if (!recoveredInfo?.resetToken) return;
    
    setIsLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        reset_token: recoveredInfo.resetToken,
        new_password: newPassword
      });
      toast.success('Password reset! You can now login.');
      setShowForgot(false);
      setRecoveredInfo(null);
      setFormData({ ...formData, email: recoveredInfo.username, password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setIsLoading(false);
    }
  };

  // Forgot Password View
  if (showForgot) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center px-4" data-testid="forgot-password">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <div className="text-center mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_d52ae5dd-568a-4669-9c8f-951ff5c53e99/artifacts/g3qmizx9_IMG_2894.png" 
              alt="Living Fruit Ministry" 
              className="h-16 w-auto mx-auto mb-4"
            />
            <h1 className="font-serif text-2xl text-charcoal">Account Recovery</h1>
            <p className="text-charcoal/60 text-sm mt-1">
              Forgot your username or password? We can help.
            </p>
          </div>

          <div className="bg-white rounded-3xl p-8 shadow-lg border border-gold/10">
            {!recoveredInfo ? (
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div>
                  <Label>Email Address</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-charcoal/40" size={18} />
                    <Input
                      type="email"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      required
                      className="pl-10 bg-cream border-gold/20 focus:border-gold"
                      placeholder="Enter your email"
                      data-testid="forgot-email"
                    />
                  </div>
                  <p className="text-xs text-charcoal/50 mt-2">
                    Enter the email associated with your account
                  </p>
                </div>

                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-burgundy hover:bg-purple text-white py-5"
                >
                  {isLoading ? 'Searching...' : 'Find My Account'}
                </Button>
              </form>
            ) : (
              <div className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                  <p className="text-green-800 font-medium">Account Found!</p>
                  <p className="text-green-700 text-sm mt-1">Hello, {recoveredInfo.name}</p>
                </div>

                <div className="bg-cream rounded-xl p-4">
                  <Label className="text-charcoal/70">Your Username</Label>
                  <p className="text-xl font-mono font-bold text-burgundy">{recoveredInfo.username}</p>
                </div>

                <div className="border-t border-gold/10 pt-4">
                  <Label>Set New Password</Label>
                  <div className="relative mt-2">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-charcoal/40" size={18} />
                    <Input
                      type="password"
                      id="new-password"
                      className="pl-10 bg-cream border-gold/20 focus:border-gold"
                      placeholder="Enter new password"
                      data-testid="new-password"
                    />
                  </div>
                  <Button
                    type="button"
                    onClick={() => {
                      const newPwd = document.getElementById('new-password').value;
                      if (newPwd.length < 4) {
                        toast.error('Password must be at least 4 characters');
                        return;
                      }
                      handleResetPassword(newPwd);
                    }}
                    disabled={isLoading}
                    className="w-full mt-3 bg-burgundy hover:bg-purple text-white"
                  >
                    {isLoading ? 'Resetting...' : 'Reset Password'}
                  </Button>
                </div>
              </div>
            )}

            <button
              type="button"
              onClick={() => { setShowForgot(false); setRecoveredInfo(null); }}
              className="flex items-center justify-center gap-2 w-full mt-6 text-sm text-charcoal/60 hover:text-charcoal transition-colors"
            >
              <ArrowLeft size={16} />
              Back to Login
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center px-4" data-testid="admin-login">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_d52ae5dd-568a-4669-9c8f-951ff5c53e99/artifacts/g3qmizx9_IMG_2894.png" 
            alt="Living Fruit Ministry" 
            className="h-16 w-auto mx-auto mb-4"
          />
          <h1 className="font-serif text-2xl text-charcoal">Admin Portal</h1>
          <p className="text-charcoal/60 text-sm mt-1">
            {isRegister ? 'Create your admin account' : 'Sign in to manage your site'}
          </p>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-3xl p-8 shadow-lg border border-gold/10">
          {/* Google Login */}
          <Button
            type="button"
            onClick={handleGoogleLogin}
            className="w-full bg-white border border-charcoal/20 text-charcoal hover:bg-cream mb-6 py-5"
            data-testid="google-login-btn"
          >
            <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </Button>

          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-charcoal/10"></div>
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-white px-2 text-charcoal/40">or</span>
            </div>
          </div>

          {/* Email Login Form */}
          <form onSubmit={handleEmailLogin} className="space-y-4">
            {isRegister && (
              <div>
                <Label>Full Name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 text-charcoal/40" size={18} />
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required={isRegister}
                    className="pl-10 bg-cream border-gold/20 focus:border-gold"
                    placeholder="Your name"
                    data-testid="register-name"
                  />
                </div>
              </div>
            )}
            
            <div>
              <Label>Email or Username</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-charcoal/40" size={18} />
                <Input
                  type="text"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                  className="pl-10 bg-cream border-gold/20 focus:border-gold"
                  placeholder="username or email"
                  data-testid="login-email"
                />
              </div>
            </div>
            
            <div>
              <Label>Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-charcoal/40" size={18} />
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  required
                  className="pl-10 pr-10 bg-cream border-gold/20 focus:border-gold"
                  placeholder="••••••••"
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-charcoal/40 hover:text-charcoal"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={isLoading}
              className="w-full bg-burgundy hover:bg-purple text-white py-5"
              data-testid="submit-login"
            >
              {isLoading ? 'Please wait...' : (isRegister ? 'Create Account' : 'Sign In')}
            </Button>
          </form>

          {/* Forgot Password */}
          {!isRegister && (
            <button
              type="button"
              onClick={() => setShowForgot(true)}
              className="flex items-center justify-center gap-2 w-full mt-4 text-sm text-charcoal/50 hover:text-burgundy transition-colors"
              data-testid="forgot-password-link"
            >
              <HelpCircle size={14} />
              Forgot username or password?
            </button>
          )}

          {/* Toggle Register/Login */}
          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => setIsRegister(!isRegister)}
              className="text-sm text-burgundy hover:text-purple transition-colors"
              data-testid="toggle-auth-mode"
            >
              {isRegister ? 'Already have an account? Sign in' : 'Need an account? Register'}
            </button>
          </div>
        </div>

        {/* Back to site */}
        <div className="text-center mt-6">
          <a href="/" className="text-sm text-charcoal/50 hover:text-charcoal transition-colors">
            ← Back to website
          </a>
        </div>
      </motion.div>
    </div>
  );
}
